'use client';

/**
 * Pricing Agent Panel Component
 *
 * AI-powered pricing negotiation agent interface:
 * - View and initiate automated pricing negotiations
 * - Monitor active agent sessions
 * - View negotiation history and decisions
 * - Manual escalation controls
 * - Pricing intelligence preview
 *
 * Integrates with backend/routers/pricing_negotiation_agent.py
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Bot,
  Play,
  Pause,
  XCircle,
  CheckCircle,
  Clock,
  AlertCircle,
  Loader2,
  DollarSign,
  TrendingUp,
  TrendingDown,
  MessageSquare,
  Mail,
  Phone,
  Globe,
  Brain,
  Activity,
  RefreshCw,
  ChevronRight,
  ArrowUpCircle,
  History,
  Target,
  Zap,
  BarChart3,
  Settings,
  AlertTriangle,
} from 'lucide-react';
import {
  pricingAgentAPI,
  AgentSession,
  PricingIntelligence,
  NegotiationHistoryItem,
  AgentMetrics,
  CommunicationChannel,
  getAgentStateLabel,
  getAgentStateColor,
  getChannelLabel,
  getDecisionLabel,
  getDecisionColor,
  isAgentActive,
  formatTimeAgo,
} from '@/services/api/pricingAgent';
import { ServiceReferral } from '@/services/api/serviceReferrals';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface PricingAgentPanelProps {
  referralId: number;
  referral: ServiceReferral;
  providerId?: string | number;
  providerType?: string;
  providerName?: string;
  readOnly?: boolean;
  onNegotiationCompleted?: () => void;
  className?: string;
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function StateIndicator({ state }: { state: string }) {
  const color = getAgentStateColor(state as any);
  const label = getAgentStateLabel(state as any);

  const colorClasses: Record<string, string> = {
    gray: 'bg-gray-100 text-gray-700 border-gray-300',
    blue: 'bg-blue-100 text-blue-700 border-blue-300',
    yellow: 'bg-yellow-100 text-yellow-700 border-yellow-300',
    green: 'bg-green-100 text-green-700 border-green-300',
    orange: 'bg-orange-100 text-orange-700 border-orange-300',
    red: 'bg-red-100 text-red-700 border-red-300',
    purple: 'bg-purple-100 text-purple-700 border-purple-300',
  };

  return (
    <Badge variant="outline" className={`${colorClasses[color] || colorClasses.gray}`}>
      {isAgentActive(state as any) && (
        <span className="w-2 h-2 rounded-full bg-current mr-1.5 animate-pulse" />
      )}
      {label}
    </Badge>
  );
}

function ChannelIcon({ channel }: { channel: CommunicationChannel }) {
  const icons: Record<CommunicationChannel, React.ReactNode> = {
    email: <Mail className="w-4 h-4" />,
    sms: <MessageSquare className="w-4 h-4" />,
    voice: <Phone className="w-4 h-4" />,
    portal: <Globe className="w-4 h-4" />,
  };
  return icons[channel] || <MessageSquare className="w-4 h-4" />;
}

function IntelligenceCard({ intelligence }: { intelligence: PricingIntelligence }) {
  const savingsPercentage = intelligence.market_average > 0
    ? ((intelligence.market_average - intelligence.target_price) / intelligence.market_average * 100).toFixed(1)
    : '0';

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Brain className="w-4 h-4 text-purple-600" />
          Pricing Intelligence
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Market Data */}
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div className="text-center p-2 bg-gray-50 rounded">
            <p className="text-xs text-gray-500">Market Low</p>
            <p className="font-semibold text-green-600">${intelligence.market_low.toFixed(0)}</p>
          </div>
          <div className="text-center p-2 bg-blue-50 rounded border border-blue-200">
            <p className="text-xs text-gray-500">Market Avg</p>
            <p className="font-semibold text-blue-600">${intelligence.market_average.toFixed(0)}</p>
          </div>
          <div className="text-center p-2 bg-gray-50 rounded">
            <p className="text-xs text-gray-500">Market High</p>
            <p className="font-semibold text-red-600">${intelligence.market_high.toFixed(0)}</p>
          </div>
        </div>

        {/* ML Recommendation */}
        <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-purple-600" />
              <span className="text-sm font-medium">ML Recommended</span>
            </div>
            <span className="text-lg font-bold text-purple-700">
              ${intelligence.ml_recommended_rate.toFixed(0)}
            </span>
          </div>
          <div className="mt-1">
            <Progress value={intelligence.ml_confidence * 100} className="h-1.5" />
            <p className="text-xs text-gray-500 mt-1">
              {(intelligence.ml_confidence * 100).toFixed(0)}% confidence
            </p>
          </div>
        </div>

        {/* Negotiation Boundaries */}
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-500 uppercase">Negotiation Strategy</p>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Opening Price</span>
            <span className="font-medium">${intelligence.opening_price.toFixed(0)}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Target Price</span>
            <span className="font-medium text-green-600">${intelligence.target_price.toFixed(0)}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Walkaway Price</span>
            <span className="font-medium text-red-600">${intelligence.walkaway_price.toFixed(0)}</span>
          </div>
        </div>

        {/* Provider Stats */}
        {intelligence.provider_historical_acceptance_rate > 0 && (
          <div className="pt-2 border-t">
            <p className="text-xs font-medium text-gray-500 uppercase mb-2">Provider History</p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <p className="text-gray-500 text-xs">Acceptance Rate</p>
                <p className="font-medium">{(intelligence.provider_historical_acceptance_rate * 100).toFixed(0)}%</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs">Avg Rounds</p>
                <p className="font-medium">{intelligence.provider_avg_negotiation_rounds.toFixed(1)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Potential Savings */}
        <div className="p-2 bg-green-50 rounded flex items-center justify-between">
          <span className="text-sm text-green-700">Potential Savings</span>
          <span className="font-bold text-green-700">
            <TrendingDown className="w-4 h-4 inline mr-1" />
            {savingsPercentage}%
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

function SessionCard({
  session,
  onEscalate,
  onCancel,
  isLoading,
}: {
  session: AgentSession;
  onEscalate: () => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  const progressPercentage = (session.current_round / session.max_rounds) * 100;

  return (
    <Card className="border-2 border-blue-200 bg-blue-50/30">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bot className="w-5 h-5 text-blue-600" />
            <CardTitle className="text-base">Active Negotiation</CardTitle>
          </div>
          <StateIndicator state={session.state} />
        </div>
        <CardDescription className="flex items-center gap-4 mt-1">
          <span className="flex items-center gap-1">
            <ChannelIcon channel={session.channel || 'portal'} />
            {getChannelLabel(session.channel || 'portal')}
          </span>
          <span>Round {session.current_round} of {session.max_rounds}</span>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Progress */}
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-gray-600">Negotiation Progress</span>
            <span className="font-medium">{session.current_round}/{session.max_rounds}</span>
          </div>
          <Progress value={progressPercentage} className="h-2" />
        </div>

        {/* Current Offers */}
        <div className="grid grid-cols-2 gap-4">
          <div className="p-3 bg-white rounded-lg border">
            <p className="text-xs text-gray-500 mb-1">Our Offer</p>
            <p className="text-xl font-bold text-blue-600">
              ${session.our_last_offer?.toFixed(0) || '-'}
            </p>
          </div>
          <div className="p-3 bg-white rounded-lg border">
            <p className="text-xs text-gray-500 mb-1">Their Counter</p>
            <p className="text-xl font-bold text-orange-600">
              ${session.their_last_offer?.toFixed(0) || '-'}
            </p>
          </div>
        </div>

        {/* Timeline */}
        <div className="text-sm text-gray-500 flex items-center gap-4">
          <span className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            Started {formatTimeAgo(session.created_at)}
          </span>
          {session.last_activity_at && (
            <span>Last activity {formatTimeAgo(session.last_activity_at)}</span>
          )}
        </div>

        {/* Actions */}
        {!['completed', 'failed', 'escalated'].includes(session.state) && (
          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onEscalate}
              disabled={isLoading}
              className="flex-1"
            >
              <ArrowUpCircle className="w-4 h-4 mr-1" />
              Escalate
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onCancel}
              disabled={isLoading}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <XCircle className="w-4 h-4 mr-1" />
              Cancel
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function HistoryTimeline({ history }: { history: NegotiationHistoryItem[] }) {
  const { fmt } = useFormattedDate();

  if (history.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500">
        <History className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p>No negotiation history yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {history.map((item, index) => (
        <div key={index} className="flex items-start gap-3">
          <div className={`
            w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0
            ${item.actor === 'agent' ? 'bg-blue-100' : 'bg-orange-100'}
          `}>
            {item.actor === 'agent' ? (
              <Bot className="w-4 h-4 text-blue-600" />
            ) : (
              <MessageSquare className="w-4 h-4 text-orange-600" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm">
                {item.actor === 'agent' ? 'AI Agent' : 'Provider'}
              </span>
              <Badge variant="secondary" className="text-xs">
                Round {item.round}
              </Badge>
              {item.channel && (
                <Badge variant="outline" className="text-xs">
                  {getChannelLabel(item.channel)}
                </Badge>
              )}
            </div>
            <p className="text-sm text-gray-600 mt-0.5">{item.action}</p>
            {item.amount && (
              <p className="text-sm font-medium mt-1">
                ${item.amount.toFixed(2)}
              </p>
            )}
            <p className="text-xs text-gray-400 mt-1">
              {fmt.dateTime(item.timestamp)}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function PricingAgentPanel({
  referralId,
  referral,
  providerId,
  providerType,
  providerName,
  readOnly = false,
  onNegotiationCompleted,
  className = '',
}: PricingAgentPanelProps) {
  const { t } = useAppTranslation();
  // State
  const [sessions, setSessions] = useState<AgentSession[]>([]);
  const [activeSession, setActiveSession] = useState<AgentSession | null>(null);
  const [intelligence, setIntelligence] = useState<PricingIntelligence | null>(null);
  const [history, setHistory] = useState<NegotiationHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state for initiating negotiation
  const [showInitiateForm, setShowInitiateForm] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState<CommunicationChannel>('email');
  const [selectedStrategy, setSelectedStrategy] = useState<'balanced' | 'aggressive' | 'accommodating' | 'urgent'>('balanced');
  const [maxRounds, setMaxRounds] = useState(5);

  // Escalation form
  const [showEscalateForm, setShowEscalateForm] = useState(false);
  const [escalationReason, setEscalationReason] = useState('');

  // Determine effective provider info
  const effectiveProviderId = providerId || referral?.selected_provider_id;
  const effectiveProviderType = providerType || referral?.selected_provider_type || 'cbo';
  const effectiveProviderName = providerName || referral?.selected_provider_name;

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Load sessions for this referral
      let sessionsData: AgentSession[] = [];
      try {
        const response = await pricingAgentAPI.getSessionsForReferral(referralId);
        // Ensure we always have an array
        sessionsData = Array.isArray(response) ? response : [];
      } catch {
        // Sessions endpoint might not exist yet or return error
        sessionsData = [];
      }
      setSessions(sessionsData);

      // Find active session
      const active = sessionsData.find(s => isAgentActive(s.state));
      setActiveSession(active || null);

      // Load history
      try {
        const historyData = await pricingAgentAPI.getNegotiationHistory(referralId);
        // Ensure we always have an array
        setHistory(Array.isArray(historyData) ? historyData : []);
      } catch {
        // History might not exist yet
        setHistory([]);
      }

      // Load intelligence preview if we have a provider
      if (effectiveProviderId) {
        try {
          const intelData = await pricingAgentAPI.getIntelligencePreview(
            referralId,
            String(effectiveProviderId)
          );
          setIntelligence(intelData);
        } catch {
          // Intelligence might fail if ML model not available
          setIntelligence(null);
        }
      }
    } catch (err) {
      console.error('Failed to load pricing agent data:', err);
      // Don't set error - just show empty state, as agent tables might not exist yet
      setSessions([]);
      setHistory([]);
      setIntelligence(null);
    } finally {
      setLoading(false);
    }
  }, [referralId, effectiveProviderId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh active session
  useEffect(() => {
    if (!activeSession) return;

    const interval = setInterval(() => {
      loadData();
    }, 10000); // Refresh every 10 seconds when active

    return () => clearInterval(interval);
  }, [activeSession, loadData]);

  // Handlers
  const handleInitiate = async () => {
    if (!effectiveProviderId) {
      setError('No provider selected for negotiation');
      return;
    }

    setActionLoading(true);
    setError(null);
    try {
      const session = await pricingAgentAPI.initiateSession({
        referral_id: referralId,
        provider_type: effectiveProviderType,
        provider_id: effectiveProviderId,
        channel: selectedChannel,
        max_rounds: maxRounds,
        strategy: selectedStrategy,
      });
      setActiveSession(session);
      setShowInitiateForm(false);
      await loadData();
    } catch (err: any) {
      console.error('Failed to initiate negotiation:', err);
      setError(err.response?.data?.detail || 'Failed to start negotiation');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEscalate = async () => {
    if (!activeSession || !escalationReason) return;

    setActionLoading(true);
    try {
      await pricingAgentAPI.escalateSession(activeSession.id, {
        reason: escalationReason,
      });
      setShowEscalateForm(false);
      setEscalationReason('');
      await loadData();
    } catch (err: any) {
      console.error('Failed to escalate:', err);
      setError(err.response?.data?.detail || 'Failed to escalate session');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!activeSession) return;

    if (!window.confirm('Are you sure you want to cancel this negotiation?')) {
      return;
    }

    setActionLoading(true);
    try {
      await pricingAgentAPI.cancelSession(activeSession.id);
      setActiveSession(null);
      await loadData();
    } catch (err: any) {
      console.error('Failed to cancel session:', err);
      setError(err.response?.data?.detail || 'Failed to cancel session');
    } finally {
      setActionLoading(false);
    }
  };

  // Render loading state
  if (loading) {
    return (
      <div className={`flex items-center justify-center p-12 ${className}`}>
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  // Check if provider is available
  if (!effectiveProviderId) {
    return (
      <Card className={className}>
        <CardContent className="p-8 text-center">
          <Bot className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-600 font-medium">No Provider Selected</p>
          <p className="text-sm text-gray-500 mt-1">
            Select a provider in the Matching tab before starting AI-powered negotiations
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Bot className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">AI Pricing Agent</h2>
            <p className="text-sm text-gray-500">
              Automated pricing negotiation with {effectiveProviderName || 'Provider'}
            </p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={loadData}>
          <RefreshCw className="w-4 h-4 mr-1" />
          Refresh
        </Button>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Session & Actions */}
        <div className="lg:col-span-2 space-y-6">
          {/* Active Session or Start New */}
          {activeSession ? (
            <>
              <SessionCard
                session={activeSession}
                onEscalate={() => setShowEscalateForm(true)}
                onCancel={handleCancel}
                isLoading={actionLoading}
              />

              {/* Escalation Form */}
              {showEscalateForm && (
                <Card className="border-orange-200 bg-orange-50/30">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <ArrowUpCircle className="w-5 h-5 text-orange-600" />
                      Escalate to Human Review
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="escalation-reason">Escalation Reason</Label>
                      <Textarea
                        id="escalation-reason"
                        value={escalationReason}
                        onChange={(e) => setEscalationReason(e.target.value)}
                        placeholder="Describe why this needs human review..."
                        className="mt-1"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={handleEscalate}
                        disabled={!escalationReason || actionLoading}
                      >
                        {actionLoading ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <ArrowUpCircle className="w-4 h-4 mr-1" />
                        )}
                        Escalate
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setShowEscalateForm(false);
                          setEscalationReason('');
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            /* No Active Session - Show Start or Completed */
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bot className="w-5 h-5 text-purple-600" />
                  Start AI Negotiation
                </CardTitle>
                <CardDescription>
                  Launch an AI-powered pricing negotiation with the provider
                </CardDescription>
              </CardHeader>
              <CardContent>
                {showInitiateForm ? (
                  <div className="space-y-4">
                    {/* Channel Selection */}
                    <div>
                      <Label>Communication Channel</Label>
                      <Select
                        value={selectedChannel}
                        onValueChange={(v) => setSelectedChannel(v as CommunicationChannel)}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="email">
                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4" />
                              Email
                            </div>
                          </SelectItem>
                          <SelectItem value="sms">
                            <div className="flex items-center gap-2">
                              <MessageSquare className="w-4 h-4" />
                              SMS
                            </div>
                          </SelectItem>
                          <SelectItem value="voice">
                            <div className="flex items-center gap-2">
                              <Phone className="w-4 h-4" />
                              Voice AI
                            </div>
                          </SelectItem>
                          <SelectItem value="portal">
                            <div className="flex items-center gap-2">
                              <Globe className="w-4 h-4" />
                              Portal
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Strategy Selection */}
                    <div>
                      <Label>Negotiation Strategy</Label>
                      <Select
                        value={selectedStrategy}
                        onValueChange={(v) => setSelectedStrategy(v as any)}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="balanced">
                            <div className="flex items-center gap-2">
                              <Target className="w-4 h-4 text-blue-600" />
                              Balanced - Fair market rate
                            </div>
                          </SelectItem>
                          <SelectItem value="aggressive">
                            <div className="flex items-center gap-2">
                              <TrendingDown className="w-4 h-4 text-green-600" />
                              Aggressive - Lower offers
                            </div>
                          </SelectItem>
                          <SelectItem value="accommodating">
                            <div className="flex items-center gap-2">
                              <TrendingUp className="w-4 h-4 text-orange-600" />
                              Accommodating - Quick agreement
                            </div>
                          </SelectItem>
                          <SelectItem value="urgent">
                            <div className="flex items-center gap-2">
                              <Zap className="w-4 h-4 text-red-600" />
                              Urgent - Fast resolution
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Max Rounds */}
                    <div>
                      <Label>Maximum Rounds</Label>
                      <Select
                        value={String(maxRounds)}
                        onValueChange={(v) => setMaxRounds(Number(v))}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="3">3 rounds</SelectItem>
                          <SelectItem value="5">5 rounds (recommended)</SelectItem>
                          <SelectItem value="7">7 rounds</SelectItem>
                          <SelectItem value="10">10 rounds</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-2">
                      <Button
                        onClick={handleInitiate}
                        disabled={actionLoading || readOnly}
                        className="flex-1"
                      >
                        {actionLoading ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <Play className="w-4 h-4 mr-1" />
                        )}
                        Start Negotiation
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => setShowInitiateForm(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    {sessions.some(s => s.state === 'completed') ? (
                      <div>
                        <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
                        <p className="font-medium text-green-700">Negotiation Completed</p>
                        <p className="text-sm text-gray-500 mt-1">
                          Agreement reached at ${sessions.find(s => s.state === 'completed')?.final_amount?.toFixed(2)}
                        </p>
                        <Button
                          variant="outline"
                          className="mt-4"
                          onClick={() => setShowInitiateForm(true)}
                          disabled={readOnly}
                        >
                          <RefreshCw className="w-4 h-4 mr-1" />
                          Start New Negotiation
                        </Button>
                      </div>
                    ) : sessions.some(s => s.state === 'escalated') ? (
                      <div>
                        <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-orange-500" />
                        <p className="font-medium text-orange-700">Escalated for Review</p>
                        <p className="text-sm text-gray-500 mt-1">
                          A previous negotiation was escalated for human review
                        </p>
                        <Button
                          variant="outline"
                          className="mt-4"
                          onClick={() => setShowInitiateForm(true)}
                          disabled={readOnly}
                        >
                          <Play className="w-4 h-4 mr-1" />
                          Try Again
                        </Button>
                      </div>
                    ) : (
                      <div>
                        <Bot className="w-12 h-12 mx-auto mb-3 text-purple-400" />
                        <p className="text-gray-600">
                          Let AI handle pricing negotiations automatically
                        </p>
                        <Button
                          className="mt-4"
                          onClick={() => setShowInitiateForm(true)}
                          disabled={readOnly}
                        >
                          <Play className="w-4 h-4 mr-1" />
                          Configure & Start
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Negotiation History */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <History className="w-5 h-5" />
                Negotiation History
              </CardTitle>
            </CardHeader>
            <CardContent>
              <HistoryTimeline history={history} />
            </CardContent>
          </Card>

          {/* Previous Sessions */}
          {sessions.filter(s => s.id !== activeSession?.id).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Previous Sessions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {sessions
                    .filter(s => s.id !== activeSession?.id)
                    .map(session => (
                      <div
                        key={session.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <StateIndicator state={session.state} />
                          <div className="text-sm">
                            <p className="font-medium">
                              {session.final_amount
                                ? `$${session.final_amount.toFixed(2)}`
                                : 'No agreement'}
                            </p>
                            <p className="text-gray-500">
                              {session.current_round} round{session.current_round !== 1 ? 's' : ''} - {getChannelLabel(session.channel || 'portal')}
                            </p>
                          </div>
                        </div>
                        <span className="text-xs text-gray-400">
                          {formatTimeAgo(session.created_at)}
                        </span>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Intelligence */}
        <div className="space-y-6">
          {intelligence ? (
            <IntelligenceCard intelligence={intelligence} />
          ) : (
            <Card>
              <CardContent className="p-6 text-center">
                <Brain className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm text-gray-500">
                  Pricing intelligence unavailable
                </p>
              </CardContent>
            </Card>
          )}

          {/* Quick Stats */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                Session Stats
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-2 bg-gray-50 rounded text-center">
                  <p className="text-2xl font-bold text-gray-700">{sessions.length}</p>
                  <p className="text-xs text-gray-500">Total Sessions</p>
                </div>
                <div className="p-2 bg-green-50 rounded text-center">
                  <p className="text-2xl font-bold text-green-700">
                    {sessions.filter(s => s.state === 'completed').length}
                  </p>
                  <p className="text-xs text-gray-500">Successful</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default PricingAgentPanel;
